import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface UserApproval {
  id: string;
  user_id: string;
  status: ApprovalStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  // Joined profile data
  user_email?: string;
  user_name?: string;
}

export function useUserApproval() {
  const { user } = useAuth();
  const [approval, setApproval] = useState<UserApproval | null>(null);
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);

  const fetchApprovalStatus = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_approvals')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setApproval(data as UserApproval);
        setIsApproved(data.status === 'approved');
      } else {
        // No approval record yet (might be super admin or record pending creation)
        // Check via RPC function
        const { data: approved, error: rpcError } = await supabase
          .rpc('is_user_approved', { check_user_id: user.id });
        
        if (rpcError) throw rpcError;
        setIsApproved(approved);
      }
    } catch (error: any) {
      console.error('Error fetching approval status:', error);
      // Default to not approved on error for safety
      setIsApproved(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchApprovalStatus();
  }, [fetchApprovalStatus]);

  return {
    approval,
    loading,
    isApproved,
    refetch: fetchApprovalStatus,
  };
}

export function useAdminApprovals() {
  const [pendingApprovals, setPendingApprovals] = useState<UserApproval[]>([]);
  const [allApprovals, setAllApprovals] = useState<UserApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch approvals with profile info
      const { data, error } = await supabase
        .from('user_approvals')
        .select(`
          *,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const approvals = (data || []).map((item: any) => ({
        ...item,
        user_email: item.profiles?.email,
        user_name: item.profiles?.full_name,
      })) as UserApproval[];

      setAllApprovals(approvals);
      setPendingApprovals(approvals.filter(a => a.status === 'pending'));
    } catch (error: any) {
      console.error('Error fetching approvals:', error);
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const sendApprovalNotification = async (userId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      await supabase.functions.invoke('send-approval-notification', {
        body: { userId, status, rejectionReason },
      });
    } catch (error) {
      console.error('Error sending approval notification:', error);
      // Don't fail the approval process if email fails
    }
  };

  const approveUser = async (userId: string) => {
    setProcessing(userId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('user_approvals')
        .update({
          status: 'approved',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: null,
        })
        .eq('user_id', userId);

      if (error) throw error;

      // Send email notification
      await sendApprovalNotification(userId, 'approved');

      toast.success('User approved successfully');
      await fetchApprovals();
    } catch (error: any) {
      console.error('Error approving user:', error);
      toast.error('Failed to approve user');
    } finally {
      setProcessing(null);
    }
  };

  const rejectUser = async (userId: string, reason?: string) => {
    setProcessing(userId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('user_approvals')
        .update({
          status: 'rejected',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason || null,
        })
        .eq('user_id', userId);

      if (error) throw error;

      // Send email notification
      await sendApprovalNotification(userId, 'rejected', reason);

      toast.success('User rejected');
      await fetchApprovals();
    } catch (error: any) {
      console.error('Error rejecting user:', error);
      toast.error('Failed to reject user');
    } finally {
      setProcessing(null);
    }
  };

  return {
    pendingApprovals,
    allApprovals,
    loading,
    processing,
    approveUser,
    rejectUser,
    refetch: fetchApprovals,
    pendingCount: pendingApprovals.length,
  };
}
